/**
 * LinkedIn links for employers named in Career Intel.
 *
 * Display-only. Nothing here scores or is sent to the engine.
 *
 * Two destinations per company:
 *  - **Company page** — the employer's LinkedIn profile.
 *  - **Jobs** — that employer's live openings.
 *
 * We only claim a company slug when we actually know it. For everything else
 * the link falls back to a LinkedIn *search* scoped to the company name, which
 * always resolves to something useful rather than a 404 on a guessed slug.
 */

/** Employers we know the real LinkedIn slug for. Lower-cased keys. */
const KNOWN_SLUGS: Record<string, string> = {
  google: "google",
  alphabet: "google",
  microsoft: "microsoft",
  amazon: "amazon",
  "amazon web services": "amazon-web-services",
  aws: "amazon-web-services",
  apple: "apple",
  meta: "meta",
  facebook: "meta",
  netflix: "netflix",
  nvidia: "nvidia",
  ibm: "ibm",
  intel: "intel-corporation",
  oracle: "oracle",
  salesforce: "salesforce",
  adobe: "adobe",
  sap: "sap",
  cisco: "cisco",
  qualcomm: "qualcomm",
  samsung: "samsung-electronics",
  uber: "uber-com",
  openai: "openai",
  anthropic: "anthropic",
  stripe: "stripe",
  atlassian: "atlassian",
  spotify: "spotify",
  linkedin: "linkedin",
  // India
  infosys: "infosys",
  tcs: "tata-consultancy-services",
  "tata consultancy services": "tata-consultancy-services",
  wipro: "wipro",
  "hcl tech": "hcl-technologies",
  "hcl technologies": "hcl-technologies",
  "tech mahindra": "tech-mahindra",
  accenture: "accenture",
  deloitte: "deloitte",
  ey: "ernstandyoung",
  "ernst & young": "ernstandyoung",
  kpmg: "kpmg",
  pwc: "pwc",
  capgemini: "capgemini",
  cognizant: "cognizant",
  mindtree: "ltimindtree",
  ltimindtree: "ltimindtree",
  flipkart: "flipkart",
  razorpay: "razorpay",
  phonepe: "phonepe",
  paytm: "paytm",
  zerodha: "zerodha",
  swiggy: "swiggy-in",
  zomato: "zomato",
  freshworks: "freshworks-inc",
  zoho: "zoho",
  ola: "olacabs",
  byju: "byjus",
  "byju's": "byjus",
  unacademy: "unacademy",
  cred: "cred-club",
  meesho: "meesho",
  nykaa: "nykaa",
  dream11: "dream11",
  postman: "postman-platform",
  browserstack: "browserstack",
  reliance: "reliance-industries-limited",
  "reliance industries": "reliance-industries-limited",
  "tata group": "tata-group",
  mahindra: "mahindra-rise",
  "larsen & toubro": "larsen-toubro-limited",
  "l&t": "larsen-toubro-limited",
  hdfc: "hdfc-bank",
  "hdfc bank": "hdfc-bank",
  "icici bank": "icici-bank",
  "axis bank": "axis-bank",
  "state bank of india": "state-bank-of-india",
  sbi: "state-bank-of-india",
  "kotak mahindra bank": "kotak-mahindra-bank",
  airtel: "bharti-airtel",
  "bharti airtel": "bharti-airtel",
  jio: "reliance-jio",
  "reliance jio": "reliance-jio",
  hul: "hindustan-unilever-limited",
  "hindustan unilever": "hindustan-unilever-limited",
  itc: "itc-limited",
  "asian paints": "asian-paints",
  "aditya birla group": "aditya-birla-group",
  drdo: "drdo",
  isro: "isro",
  "bharat electronics": "bharat-electronics-limited",
  "sun pharma": "sun-pharmaceutical-industries-ltd",
  cipla: "cipla",
  "dr reddy's": "dr-reddys-laboratories",
  biocon: "biocon",
  apollo: "apollo-hospitals",
  "apollo hospitals": "apollo-hospitals",
  fortis: "fortis-healthcare",
  "max healthcare": "max-healthcare",
  aiims: "aiims-new-delhi",
};

/** Normalise a display name: drop suffixes, punctuation and extra spacing. */
export function normaliseCompany(name: string): string {
  return name
    .toLowerCase()
    .replace(/\([^)]*\)/g, " ")
    .replace(/\b(pvt|private|ltd|limited|inc|llc|llp|plc|corp|corporation|co|group|technologies|labs)\b\.?/g, " ")
    .replace(/[^a-z0-9& ']/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** The LinkedIn slug for this employer, or null when we don't know it. */
export function companySlug(name: string): string | null {
  const key = normaliseCompany(name);
  return KNOWN_SLUGS[key] ?? null;
}

export type EmployerLinkKind = "company" | "jobs";

export interface EmployerLink {
  /** Where the click goes. */
  href: string;
  /** True when we resolved a real LinkedIn company page. */
  exact: boolean;
  /** Human-readable description of what will open — used in the tooltip. */
  query: string;
}

/**
 * The employer's LinkedIn company page, or a LinkedIn company search for the
 * name when no slug is known.
 */
export function companyPageLink(name: string): EmployerLink {
  const slug = companySlug(name);
  if (slug) {
    return {
      href: `https://www.linkedin.com/company/${slug}/`,
      exact: true,
      query: "LinkedIn company page",
    };
  }
  return {
    href: `https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(name)}`,
    exact: false,
    query: `LinkedIn search: companies matching "${name}"`,
  };
}

/**
 * The employer's jobs. Known employers go straight to their jobs tab; everyone
 * else gets a LinkedIn jobs search scoped to the company name, so a missing
 * slug never becomes a dead end.
 */
export function companyJobsLink(name: string, role?: string): EmployerLink {
  const slug = companySlug(name);
  if (slug) {
    return {
      href: `https://www.linkedin.com/company/${slug}/jobs/`,
      exact: true,
      query: `LinkedIn jobs at ${name}`,
    };
  }
  const keywords = role ? `${name} ${role}` : name;
  return {
    href: `https://www.linkedin.com/jobs/search/?keywords=${encodeURIComponent(keywords)}`,
    exact: false,
    query: `LinkedIn jobs search: "${keywords}"`,
  };
}

export function employerLink(
  name: string,
  kind: EmployerLinkKind,
  role?: string,
): EmployerLink {
  return kind === "jobs" ? companyJobsLink(name, role) : companyPageLink(name);
}
